import { useState } from "react";
import styles from "./ReviewForm.module.css";
import StarRating from "./StarRating";
import { createReview } from "../services/reviewService";
import { LeafIcon } from "./icons";

const emptyForm = { name: "", rating: 0, feedback: "", phone: "" };

function ReviewForm() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleRating = (n) => {
    setForm((f) => ({ ...f, rating: n }));
    if (errors.rating) setErrors((er) => ({ ...er, rating: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.rating) next.rating = "Pick a star rating.";
    if (!form.name.trim()) next.name = "Let us know your name.";
    if (!form.feedback.trim()) next.feedback = "A quick word on how it went.";
    if (!form.phone.trim()) next.phone = "A contact number, just for us.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 12000)
      );
      await Promise.race([createReview(form), timeout]);
      setSuccess(true);
    } catch (err) {
      setFormError("Couldn't send that just now — please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className={`${styles.form} ${styles.successCard}`}>
        <LeafIcon className={styles.successIcon} />
        <h3>Thanks for the feedback</h3>
        <p>
          We read every review — yours will show up on the site once it's
          been checked over.
        </p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.grid}>
        <div className={styles.field}>
          <label>
            Your rating <span className={styles.required}>*</span>
          </label>
          <StarRating value={form.rating} onChange={handleRating} size={30} />
          {errors.rating && <span className={styles.errorText}>{errors.rating}</span>}
        </div>

        <div className={styles.row2}>
          <div className={styles.field}>
            <label htmlFor="name">
              Name <span className={styles.required}>*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
            />
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="phone">
              Contact number <span className={styles.required}>*</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
            />
            {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
          </div>
        </div>
        <p className={styles.privacyNote}>
          Your number is only ever seen by us, in case we'd like to follow up
          — it's never shown on the site.
        </p>

        <div className={styles.field}>
          <label htmlFor="feedback">
            Your feedback <span className={styles.required}>*</span>
          </label>
          <textarea
            id="feedback"
            name="feedback"
            value={form.feedback}
            onChange={handleChange}
            placeholder="How did the job go?"
          />
          {errors.feedback && <span className={styles.errorText}>{errors.feedback}</span>}
        </div>
      </div>

      <div className={styles.submitRow}>
        <button type="submit" className="btn btnPrimary" disabled={submitting}>
          {submitting ? "Sending…" : "Submit review"}
        </button>
        {formError && <span className={styles.formError}>{formError}</span>}
      </div>
    </form>
  );
}

export default ReviewForm;
